import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';

import {
  Text, Picture, ShareButton
} from '@comicrelief/component-library';

import Layout from '../../components/Layout/Layout';
import { SEO, SEO_PAGE_TYPES } from '../../components/SEO/SEO';
import renderRichText from '../../components/RichText/RichText';
import NewsBlock from '../../components/NewsBlock/NewsBlock';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import {
  Wrapper, Container, Header, Image, Body
} from './PressRelease.styles';
import ImagePropType from '../../commonPropTypes/commonPropTypes';

const PressRelease = ({ data }) => {
  const {
    article: { edges },
    related: { edges: relatedPressReleases }
  } = data;

  const post = edges[0].node;

  const {
    title, displayDate, image, body
  } = post;

  const thisImageData = getImage(image);
  const thisImageSrcSet = thisImageData?.images?.sources[0]?.srcSet;
  const thisImageLow = thisImageData?.placeholder?.fallback;
  const thisImageDesc = image?.description ?? '';

  // As ALL press releases are 'children' of the /news page, we'll just hardcode this,
  // rather than using a per-article field set in the CMS:
  const parentPages = [
    { title: 'About Us', path: 'about' },
    { title: 'News', path: 'news' },
    { title: 'Press Releases', path: 'press-releases' }
  ];

  return (
    <Layout>
      <Wrapper data-test="press-release">

        <Breadcrumb thisPageTitle={title} parentPages={parentPages} />

        <Header>
          <Text tag="h1" color="black">
            {title}
          </Text>
          <Text tag="p">
            {displayDate}
          </Text>
        </Header>
        <Container>
          <div data-test="social-share">
            <ShareButton />
          </div>
          {image ? (
            <Image>
              <Picture
                alt={thisImageDesc}
                images={thisImageSrcSet}
                imageLow={thisImageLow}
              />
            </Image>
          ) : null}
          <Body>{body ? renderRichText(body, 'white', 'news') : null}</Body>
        </Container>
        <div data-test="press-release-related">
          <NewsBlock data={relatedPressReleases} isPressRelease relatedArticlesRow />
        </div>
      </Wrapper>
    </Layout>
  );
};

PressRelease.propTypes = {
  data: PropTypes.shape({
    article: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            body: PropTypes.shape({
              raw: PropTypes.string
            }),
            displayDate: PropTypes.string.isRequired,
            image: ImagePropType,
            path: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            summary: PropTypes.shape({
              summary: PropTypes.string
            })

          })
        })
      )
    }).isRequired,
    related: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            body: PropTypes.shape({
              raw: PropTypes.string
            }),
            displayDate: PropTypes.string.isRequired,
            image: ImagePropType,
            path: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            summary: PropTypes.shape({
              summary: PropTypes.string
            })
          })
        })
      )
    }).isRequired
  }).isRequired
};

export default PressRelease;

export const Head = props => (
  <SEO
    allData={props}
    pageType={SEO_PAGE_TYPES.PRESS_RELEASE}
    listAsArticle
    excludeFromSearchEngineIndexing
  >
    {(thisSiteUrl, linkedDataObj) => [
      <script type="application/ld+json" key="article">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          author: 'Comic Relief',
          headline: linkedDataObj.title,
          description: linkedDataObj.description,
          datePublished: linkedDataObj.articleDate,
          mainEntityOfPage: linkedDataObj.url,
          image: {
            '@type': 'imageObject',
            url: linkedDataObj.image
          },
          publisher: {
            '@type': 'Organization',
            name: 'Comic Relief',
            logo: {
              '@type': 'imageObject',
              url: `${thisSiteUrl}/assets/logo.jpg`
            }
          }
        })}
      </script>
    ]}
  </SEO>
);

export const pageQuery = graphql`
  query getPressRelease($slug: String, $type: String) {
    article: allContentfulPageArticle(filter: {path: {eq: $slug}}) {
      edges {
        node {
          ...ArticleList
        }
      }
    }
    related: allContentfulPageArticle(
      filter: {type: {eq: $type}}
      limit: 3
      sort: {displayDate: DESC}
    ) {
      edges {
        node {
          ...ArticleList
        }
      }
    }
  } 
`;
